'use client'

import { useCallback, useRef } from 'react'
import { ChevronLeft } from 'lucide-react'
import Turnstile, { type TurnstileHandle } from '../../../Turnstile'
import type { ProcessedTheme } from '../../../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses, compactDate } from '../../../ProcessedEntry/DomainEntry/processedTypes'
import { CampaignForm } from '../shared/CampaignForm'
import type { CttCampaign } from '../../trustCenterTypes'
import { parseDbTimestamp } from '@/lib/dates'

const TURNSTILE_SITEKEY = process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_TF_CENTER_GETENTRIES_KEY ?? ''

export function CttBrandCampaignDetail({
  entry,
  theme,
  onBack,
  onSaved,
}: {
  entry: CttCampaign
  theme: ProcessedTheme
  onBack: () => void
  onSaved: () => void
}) {
  const isDark = theme === 'dark'
  const c = watchClasses(theme)
  const border = 'border-border'
  const muted  = 'text-muted-foreground'

  const turnstileRef    = useRef<TurnstileHandle>(null)
  const pendingTokenRef = useRef<((token: string) => void) | null>(null)

  const handleVerify = useCallback((token: string) => {
    pendingTokenRef.current?.(token)
    pendingTokenRef.current = null
  }, [])

  const getToken = useCallback((): Promise<string> =>
    new Promise((resolve) => {
      pendingTokenRef.current = resolve
      turnstileRef.current?.reset()
      turnstileRef.current?.run()
    })
  , [])

  const dayMonth = (() => {
    const d = parseDbTimestamp(entry.created_at)
    if (!d) return '—'
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`
  })()

  return (
    <div className={`min-h-screen bg-background`}>
      <Turnstile ref={turnstileRef} sitekey={TURNSTILE_SITEKEY} onVerify={handleVerify} />

      {/* Top bar */}
      <div className={`flex items-center gap-3 border-b px-6 py-3 ${border} bg-card`}>
        <button
          onClick={onBack}
          className={`flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest transition-colors hover:text-signal ${muted}`}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          Back
        </button>
        <span className={`font-mono text-[10px] text-subtle`}>/</span>
        <span className={`font-mono text-[10px] uppercase tracking-widest ${muted}`}>Brand campaign</span>
      </div>

      {/* Bento grid */}
      <article className={`border-b overflow-hidden ${border} bg-card`}>
        <div className="grid grid-cols-[auto_1fr_1fr_auto]">

          {/* Cell 1: chips + date */}
          <div className={`flex flex-col justify-between gap-6 px-6 py-6 border-r ${border}`}>
            <div className="flex flex-col gap-2">
              <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 w-fit ${c.line} ${c.soft}`}>
                <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Brand</span>
                <span className={`font-mono text-[10px] ${c.muted}`}>{entry.id.slice(0, 8)}…</span>
              </span>
              {entry.processed_entry_id && (
                <span className={`font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 w-fit bg-indigo-50 border-indigo-200 text-indigo-700`}>
                  Processed
                </span>
              )}
              <span className={`font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 w-fit ${entry.is_active ? ('bg-success-surface border-success/40 text-success') : ('bg-secondary border-border text-muted-foreground')}`}>
                {entry.is_active ? 'active' : 'inactive'}
              </span>
              <span className={`text-xs ${c.muted}`}>published {compactDate(entry.created_at)}</span>
            </div>
            <div>
              <div className={`font-mono text-[34px] leading-none tracking-tight ${c.text}`}>{dayMonth}</div>
              <div className={`mt-1 font-mono text-[10px] uppercase tracking-[0.3em] ${c.label}`}>published</div>
            </div>
          </div>

          {/* Cell 2: headline */}
          <div className={`flex items-start px-6 py-6 border-r ${border}`}>
            <h2 className={`text-xl font-semibold leading-snug tracking-[-0.01em] ${c.text}`}>
              "{entry.headline}"
            </h2>
          </div>

          {/* Cell 3: description */}
          <div className={`flex flex-col justify-between gap-4 px-6 py-6 border-r ${border}`}>
            {entry.description && (
              <p className={`text-[15px] leading-7 ${c.muted}`}>{entry.description}</p>
            )}
            <div className="flex flex-wrap gap-2">
              <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${c.line} ${c.soft}`}>
                {entry.image_count} image{entry.image_count !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          {/* Cell 4: images preview */}
          <div className="flex flex-wrap items-start gap-2 p-3 max-w-[240px]">
            {entry.images.slice(0, 3).map((img) => (
              <img
                key={img.r2_key}
                src={img.url}
                alt={img.alt_text || ''}
                className={`h-16 w-auto rounded border object-contain ${border}`}
              />
            ))}
          </div>

        </div>
      </article>

      {/* Shared campaign form */}
      <div className="px-8 py-8">
        <CampaignForm
          config={{ mode: 'edit', campaign: entry }}
          theme={theme}
          getToken={getToken}
          onBack={onBack}
          onDone={onSaved}
        />
      </div>
    </div>
  )
}
